import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiHandle from "services/apiHandle";

/**
 * Ads Analytics Slice
 * Handles ad analytics for service providers (mechanic role)
 * and popup ad system for consumers (driver / fleet_manager / truck_owner).
 */

// ─── SP Ad Analytics ─────────────────────────────────────────────

/** Fetch all ads analytics for the current service provider */
export const fetchAdsAnalytics = createAsyncThunk(
  "ads/fetchAdsAnalytics",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/ads-analytics/my-ads", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch ads analytics");
    }
  }
);

/** Fetch detailed analytics for a single service's ad */
export const fetchAdDetail = createAsyncThunk(
  "ads/fetchAdDetail",
  async (serviceId, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/ads-analytics/service/${serviceId}`, "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch ad details");
    }
  }
);

/** Track an ad metric (click / impression) */
export const trackAdMetric = createAsyncThunk(
  "ads/trackAdMetric",
  async ({ serviceId, type = "click" }, { rejectWithValue }) => {
    try {
      await apiHandle("/ads-analytics/track", "POST", { serviceId, type });
      return { serviceId, type };
    } catch (err) {
      return rejectWithValue(err?.message || "Track failed");
    }
  }
);

// ─── Consumer Popup Ad ───────────────────────────────────────────

const POPUP_INTERVAL_KEY = "popup_last_shown_";
const DEFAULT_INTERVAL_HOURS = 24;

/** Fetch a premium popup ad for the consumer's location */
export const fetchPopupAd = createAsyncThunk(
  "ads/fetchPopupAd",
  async ({ latitude, longitude } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (latitude) params.latitude = latitude;
      if (longitude) params.longitude = longitude;

      const res = await apiHandle("/services/popup-ad", "GET", null, {}, params);

      const ad = res?.data || null;
      if (!ad || !ad._id) return null;

      // Interval check (client-side frequency capping)
      const intervalHours =
        ad.popupIntervalHours ?? ad.featuredDetails?.popupIntervalHours ?? DEFAULT_INTERVAL_HOURS;
      const intervalMs = intervalHours * 3600 * 1000;
      const lastShownStr = localStorage.getItem(POPUP_INTERVAL_KEY + ad._id);

      if (lastShownStr) {
        const elapsed = Date.now() - parseInt(lastShownStr, 10);
        if (elapsed < intervalMs) {
          return { ad, intervalBlocked: true };
        }
      }

      // Save shown timestamp
      localStorage.setItem(POPUP_INTERVAL_KEY + ad._id, Date.now().toString());
      return ad;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch popup ad");
    }
  }
);

/** Track popup ad click */
export const trackPopupAdClick = createAsyncThunk(
  "ads/trackPopupAdClick",
  async ({ serviceId, type = "click" }) => {
    if (!serviceId || serviceId.length !== 24) return { serviceId, type, skipped: true };
    try {
      await apiHandle("/ads-analytics/track", "POST", { serviceId, type });
    } catch (_) {
      // Non-critical
    }
    return { serviceId, type };
  }
);

// ─── Slice ───────────────────────────────────────────────────────

const adsSlice = createSlice({
  name: "ads",
  initialState: {
    // SP analytics
    adsList: [],
    adDetail: null,
    analyticsLoading: false,
    detailLoading: false,
    error: null,

    // Consumer popup
    popupAd: null,
    popupVisible: false,
    popupLoading: false,
    lastShownAdId: null,
  },
  reducers: {
    hidePopup(state) {
      state.popupVisible = false;
      state.lastShownAdId = null;
    },
    clearPopupAd(state) {
      state.popupAd = null;
      state.popupVisible = false;
      state.lastShownAdId = null;
    },
    clearAdDetail(state) {
      state.adDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchAdsAnalytics ────────────────────────────────
      .addCase(fetchAdsAnalytics.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(fetchAdsAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.adsList = action.payload?.data || [];
      })
      .addCase(fetchAdsAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload;
      })

      // ── fetchAdDetail ────────────────────────────────────
      .addCase(fetchAdDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchAdDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.adDetail = action.payload?.data || null;
      })
      .addCase(fetchAdDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })

      // ── fetchPopupAd ─────────────────────────────────────
      .addCase(fetchPopupAd.pending, (state) => {
        state.popupLoading = true;
      })
      .addCase(fetchPopupAd.fulfilled, (state, action) => {
        state.popupLoading = false;
        const payload = action.payload;

        if (payload && payload.intervalBlocked) {
          // Interval not met — don't show
          return;
        }

        if (payload && payload._id) {
          if (state.lastShownAdId === payload._id) return;
          state.popupAd = payload;
          state.popupVisible = true;
          state.lastShownAdId = payload._id;
        } else {
          if (!state.popupVisible) {
            state.popupAd = null;
          }
        }
      })
      .addCase(fetchPopupAd.rejected, (state) => {
        state.popupLoading = false;
        state.popupAd = null;
        state.popupVisible = false;
      });
  },
});

export const { hidePopup, clearPopupAd, clearAdDetail } = adsSlice.actions;
export default adsSlice.reducer;
